import { ChevronDownIcon } from "@heroicons/react/outline";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { WorkflowContext, useModal } from "../contexts";
import ToggleButton from "./ToggleButton";
import SmallSelectField from "./SmallSelectField";
import { getData, patchData } from "../backend";
import { PipelineStage, HubspotConfig, Pipeline } from "../interfaces";
import { Link, useRouteMatch } from "react-router-dom";
import useUser from "../hooks/useUser";
import PayWallModalPro from "./PayWallModalPro";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import WorkflowAutomationPanelIncomplete from "./WorkflowAutomationPanelIncomplete";
import WorkflowAutomationPanelManual from "./WorkflowAutomationPanelManual";


function WorkflowAutomationPanelHubspot() {
  const { workflow, mutateWorkflow, automationOptions, updateAutoCreate } = useContext(WorkflowContext);
  const { t } = useTranslation();

  const { user } = useUser();
  const { openModal } = useModal();

  const [config, setConfig] = useState<HubspotConfig | undefined>(workflow.source?.hubspotConfig);
  const [showAutoCreate, setShowAutoCreate] = useState(false);
  const { data: pipelines } = useSWR<Array<Pipeline>>(workflow.source?.hubspotConfig?.hubspotObjectTypeId === "0-3" ? `/hubspot/pipelines/` : null, getData);

  const { url } = useRouteMatch();

  useEffect(() => {
    setConfig(workflow.source?.hubspotConfig);
  }, [workflow.source?.hubspotConfig]);

  async function handleUpdateAutoCreate(autoCreate: boolean) {
    if (autoCreate && !user?.subscription?.featureHubspotAutocreate)
      return openModal(<PayWallModalPro />);

    updateAutoCreate(autoCreate);
  }

  const updateHubspotSourceConfig = useCallback((updates: any) => {
    setConfig(c => ({ ...c, ...updates }));

    patchData(`/hubspot/config/${workflow.source!.hubspotConfig?.id}/`, updates)
      .then(() => mutateWorkflow())
      .catch(console.error);

  }, [mutateWorkflow, workflow.source])

  // TODO: Do we need set the default pipeline and deal stage when the user selects a new pipeline?

  // useEffect(() => {
  //   if (!config || config.hubspotObjectTypeId !== "0-3" || pipelines) // deals
  //     return;

  //   if (pipelines.length >= 1 && !pipelines.find((p: Pipeline) => p.id === config.notificationPipeline))
  //     updateHubspotSourceConfig({ notificationPipeline: pipelines[0].id, notificationDealstage: "" })

  // }, [config, pipelines, updateHubspotSourceConfig]);

  const pipelineStages = useMemo<Array<PipelineStage>>(() => {
    return pipelines?.find(p => p.id === config?.notificationPipeline)?.stages ?? [];
  }, [pipelines, config?.notificationPipeline]);

  return (
    <>
      <div className="flex flex-col gap-2 select-none">
        {!(workflow.latestAutomation?.status === "PENDING" || workflow.latestAutomation?.status === "RUNNING") &&
          <Link to={url + "/source"} className="flex items-center text-sm w-full font-semibold hover:text-blue hover:underline cursor-pointer">
            {(automationOptions.selectedTotal ?? 0) > 0
              ? <>{t("Process")} <b className="mx-1">{automationOptions.selectedTotal}</b> {t(automationOptions.selectedTotal === 1 ? "selected item" : "selected items")} — {t("Edit selection")}</>
              : <span className="text-gray-400">{t("Select HubSpot data to start an automation")}</span>
            }
          </Link>
        }
        <WorkflowAutomationPanelManual disabled={!automationOptions.selectedTotal || automationOptions.selectedTotal === 0} />
      </div>

      <WorkflowAutomationPanelIncomplete />

      <div className="h-px bg-gray-200 my-4" />

      {/* Auto-Create Section */}
      <div className="flex flex-col gap-2">
        <button className="flex items-center justify-between w-full font-gilroy font-semibold text-sm text-gray-600 cursor-pointer" onClick={() => setShowAutoCreate(b => !b)}>
          <div className="flex items-center gap-2">
            {t("Auto-Create")}
            {workflow.autoCreate && <span className="text-xs text-green font-semibold">{t("Enabled")}</span>}
          </div>
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAutoCreate ? "rotate-180" : ""}`} />
        </button>
        {showAutoCreate &&
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <ToggleButton value={workflow.autoCreate} onChange={handleUpdateAutoCreate} bgColor="bg-green" disabled={config?.hubspotObjectTypeId !== "0-3"} />
              <span>{t("Run this workflow when deals are moved into the selected deal stage (only available for deals).")}</span>
            </div>
            {workflow.autoCreate && config &&
              <div className="flex flex-col gap-1">
                {((pipelines?.length ?? 0) > 1) &&
                  <SmallSelectField value={config.notificationPipeline} onChange={(notificationPipeline) => updateHubspotSourceConfig({ notificationPipeline, notificationDealstage: "" })} label={t("Pipeline")}>
                    {pipelines!.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </SmallSelectField>
                }
                <SmallSelectField value={!!pipelines ? config.notificationDealstage : ""} onChange={(notificationDealstage) => updateHubspotSourceConfig({ notificationPipeline: config.notificationPipeline, notificationDealstage })} label={t("Deal Stage")} invalid={config?.notificationDealstage === ""} disabled={pipelines === undefined}>
                  <>
                    <option value="" disabled>{pipelines === undefined ? t("Loading Pipelines...") : t("Please select deal stage")}</option>
                    {pipelineStages.map(ds => <option key={ds.id} value={ds.id}>{ds.label}</option>)}
                  </>
                </SmallSelectField>
              </div>
            }
          </div>
        }
      </div>
    </>
  );

}

export default WorkflowAutomationPanelHubspot;
